import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Wifi, ChefHat, Snowflake, Tv, WashingMachine, Coffee, Bath, KeyRound, ShieldCheck, BedDouble,
  Star, ArrowRight, MapPin, BadgeEuro, Heart, Sparkles,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingWidget from "@/components/BookingWidget";
import MobileBottomCTA from "@/components/MobileBottomCTA";
import Reveal from "@/components/Reveal";
import Lightbox from "@/components/Lightbox";
import { useT } from "@/i18n/LanguageContext";
import { APARTMENT_SEED } from "@/data/apartment";
import { REVIEWS } from "@/data/reviews";

const HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_prenota-diretto/artifacts/ywhdeula_vista%20skyline.png",
  "https://customer-assets.emergentagent.com/job_prenota-diretto/artifacts/uyy0a8pa_10.jpg.jpeg",
  "https://customer-assets.emergentagent.com/job_prenota-diretto/artifacts/6ptantq5_3.jpg.jpeg",
];

const AMENITIES = [
  { icon: Wifi, label: "Wi-Fi veloce" },
  { icon: ChefHat, label: "Cucina attrezzata" },
  { icon: Snowflake, label: "Aria condizionata" },
  { icon: Tv, label: "Smart TV" },
  { icon: WashingMachine, label: "Lavatrice" },
  { icon: Coffee, label: "Macchina espresso" },
  { icon: Bath, label: "Bagno privato" },
  { icon: KeyRound, label: "Self check-in" },
  { icon: ShieldCheck, label: "Rilevatore fumo" },
  { icon: BedDouble, label: "Lenzuola in lino" },
];

const FAQS = [
  { q: "Come posso prenotare l'appartamento?", a: "Utilizza la barra di ricerca, scegli le date e clicca 'Verifica disponibilità'. In pochi passaggi riceverai la conferma." },
  { q: "Quali sono i metodi di pagamento accettati?", a: "Accettiamo carte di credito tramite pagamento sicuro Stripe. È anche possibile inviare una richiesta e procedere offline." },
  { q: "Posso cancellare la prenotazione?", a: "Sì, offriamo cancellazione gratuita fino a 7 giorni prima dell'arrivo." },
  { q: "L'appartamento è adatto a famiglie?", a: "Assolutamente sì. Attico Skyline Milano ospita fino a 4 persone ed è perfetta per famiglie." },
  { q: "Come funziona il check-in?", a: "Check-in autonomo 24/7 tramite cassetta di sicurezza. Riceverai il codice via email il giorno prima dell'arrivo." },
];

export default function Home() {
  const { t } = useT();
  const [heroIdx, setHeroIdx] = useState(0);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  useEffect(() => {
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 6000);
    return () => clearInterval(id);
  }, []);

  const apt = APARTMENT_SEED;
  const openLightbox = (i) => setLightbox({ open: true, index: i });

  return (
    <div className="min-h-screen bg-cream-50 pb-20 lg:pb-0">
      <Navbar />

      {/* HERO */}
      <section data-testid="hero-section" className="relative w-full min-h-[680px] md:min-h-[760px] overflow-hidden">
        {HERO_IMAGES.map((src, i) => (
          <div
            key={src}
            className="absolute inset-0 transition-opacity duration-[1400ms]"
            style={{ opacity: heroIdx === i ? 1 : 0 }}
          >
            <img src={src} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/20 to-slate-900/60" />
          </div>
        ))}

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-32 md:pt-44 pb-12">
          <p data-testid="hero-eyebrow" className="text-xs uppercase tracking-[0.3em] text-white/90 mb-5 animate-fade-up">
            {t("hero.eyebrow")}
          </p>
          <h1 data-testid="hero-title" className="text-white text-5xl sm:text-6xl lg:text-7xl display-tight font-extrabold max-w-3xl animate-fade-up" style={{ animationDelay: "120ms" }}>
            {t("hero.title_line1")}<br />
            <span className="text-white">{t("hero.title_line2")}</span>
          </h1>
          <p data-testid="hero-subtitle" className="mt-6 text-base md:text-lg text-white/90 max-w-xl leading-relaxed animate-fade-up" style={{ animationDelay: "260ms" }}>
            {t("hero.subtitle")}
          </p>

          {/* indicators */}
          <div className="flex items-center gap-2 mt-10">
            {HERO_IMAGES.map((_, i) => (
              <button
                key={i}
                onClick={() => setHeroIdx(i)}
                aria-label={`Vai a immagine ${i + 1}`}
                className={`h-1 rounded-full transition-all ${heroIdx === i ? "bg-white w-10" : "bg-white/40 w-5"}`}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 -mt-2 md:-mt-4 pb-12 md:pb-20">
          <div className="animate-fade-up" style={{ animationDelay: "420ms" }}>
            <BookingWidget variant="hero" />
          </div>
        </div>
      </section>

      {/* APARTMENT PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32" id="appartamento">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 grid grid-cols-6 grid-rows-6 gap-3 h-[420px] md:h-[560px]">
              <button onClick={() => openLightbox(0)} className="col-span-4 row-span-6 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-navy-600">
                <img src={apt.gallery[0]} alt="" className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500" />
              </button>
              <button onClick={() => openLightbox(1)} className="col-span-2 row-span-3 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-navy-600">
                <img src={apt.gallery[1]} alt="" className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500" />
              </button>
              <button onClick={() => openLightbox(2)} className="col-span-2 row-span-3 rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-navy-600">
                <img src={apt.gallery[2]} alt="" className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500" />
              </button>
            </div>

            <div className="lg:col-span-5">
              <p className="text-xs uppercase tracking-[0.25em] text-navy-700 font-semibold mb-4">{t("sec.our_apartment")}</p>
              <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 display-tight-2">
                {t("preview.title")}
              </h2>
              <div className="flex items-center gap-4 mt-5 text-sm text-slate-600">
                <span className="flex items-center gap-1.5"><MapPin size={15} className="text-navy-600" /> Milano</span>
                <span className="flex items-center gap-1.5"><Star size={15} className="text-navy-600 fill-navy-600" /> {apt.rating} · {apt.reviews_count} {t("apt.reviews_label")}</span>
              </div>
              <p className="mt-6 text-slate-600 leading-relaxed text-base">
                {apt.short_description}
              </p>

              <div className="grid grid-cols-2 gap-4 mt-8">
                {[
                  { label: t("preview.guests"), value: `${t("preview.up_to")} ${apt.max_guests}` },
                  { label: t("preview.bedrooms"), value: `${apt.bedrooms}` },
                  { label: t("preview.beds"), value: `${apt.beds}` },
                  { label: t("preview.baths"), value: `${apt.bathrooms}` },
                ].map((s) => (
                  <div key={s.label} className="bg-white border border-cream-100 rounded-2xl px-5 py-4">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-600">{s.label}</p>
                    <p className="text-2xl font-bold text-slate-900 tracking-tight mt-1">{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link
                  to={`/appartamento/${apt.slug}`}
                  data-testid="apt-preview-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-cream-50 px-7 py-3.5 text-sm font-medium transition-all hover:-translate-y-0.5"
                >
                  {t("preview.discover")} <ArrowRight size={15} />
                </Link>
                <p className="text-sm text-slate-600">
                  {t("preview.from")} <span className="font-semibold text-slate-900">€ {apt.price_per_night}</span> {t("preview.per_night")}
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* WHY BOOK DIRECT */}
      <section id="servizi" className="bg-white border-y border-cream-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-28">
          <Reveal>
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.25em] text-navy-700 font-semibold mb-4">{t("ben.eyebrow")}</p>
              <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 display-tight-2">
                {t("ben.title")}
              </h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-12">
            {[
              { icon: BadgeEuro, title: t("ben.1_title"), text: t("ben.1_text") },
              { icon: Heart, title: t("ben.2_title"), text: t("ben.2_text") },
              { icon: Sparkles, title: t("ben.3_title"), text: t("ben.3_text") },
            ].map((b, i) => (
              <Reveal key={b.title} delay={i * 120}>
                <div className="bg-cream-50 rounded-2xl p-8 h-full hover:-translate-y-1 transition-transform">
                  <div className="w-12 h-12 rounded-full bg-navy-50 grid place-items-center text-navy-700 mb-5">
                    <b.icon size={20} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 tracking-tight">{b.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-sm">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32">
        <Reveal>
          <div className="flex items-end justify-between mb-10 md:mb-14">
            <div className="max-w-xl">
              <p className="text-xs uppercase tracking-[0.25em] text-navy-700 font-semibold mb-4">{t("gal.eyebrow")}</p>
              <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 display-tight-2">{t("gal.title")}</h2>
            </div>
            <button onClick={() => openLightbox(0)} data-testid="gal-show-all" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-slate-900 border border-slate-900 rounded-full px-5 py-2.5 hover:bg-slate-900 hover:text-cream-50 transition-colors">
              {t("gal.show_all")} <ArrowRight size={15} />
            </button>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {apt.gallery.map((src, i) => (
            <Reveal key={src} delay={i * 80}>
              <button
                data-testid={`gal-img-${i}`}
                onClick={() => openLightbox(i)}
                className={`block rounded-2xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-navy-600 ${i === 0 ? "col-span-2 row-span-2 md:row-span-2 aspect-square" : "aspect-square"}`}
              >
                <img src={src} alt={`${i + 1}`} loading="lazy" className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-500" />
              </button>
            </Reveal>
          ))}
        </div>
        <div className="md:hidden mt-6 text-center">
          <button onClick={() => openLightbox(0)} className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 border border-slate-900 rounded-full px-6 py-3">
            {t("gal.show_all")} <ArrowRight size={15} />
          </button>
        </div>
      </section>

      {/* AMENITIES */}
      <section id="amenities" className="bg-white border-y border-cream-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-28">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 display-tight-2 max-w-2xl">
              {t("am.title")}
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mt-12">
            {AMENITIES.map((a, i) => (
              <Reveal key={a.label} delay={i * 40}>
                <div className="flex flex-col items-start gap-3 p-5 rounded-2xl bg-cream-50 hover:bg-navy-50 transition-colors h-full">
                  <a.icon size={22} className="text-navy-700" />
                  <p className="text-sm text-slate-900 font-medium">{a.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GUEST REVIEWS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-32">
        <Reveal>
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div className="max-w-xl">
              <p className="text-xs uppercase tracking-[0.25em] text-navy-700 font-semibold mb-4">{t("rev.eyebrow")}</p>
              <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 display-tight-2">
                {t("rev.title")}
              </h2>
            </div>
            <div className="flex items-center gap-2 bg-cream-100 rounded-full px-4 py-2">
              <Star size={16} className="text-navy-600 fill-navy-600" />
              <span className="text-sm font-semibold text-slate-900">5.0 / 5.0 · 42</span>
            </div>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {REVIEWS.map((r, i) => (
            <Reveal key={r.name} delay={(i % 6) * 80}>
              <div className="bg-white border border-cream-200 rounded-2xl p-7 h-full shadow-soft hover:shadow-elevate transition-shadow">
                <div className="flex items-center gap-1 text-navy-600 mb-4">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} size={13} className="fill-navy-600" />
                  ))}
                </div>
                <p className="text-[15px] text-slate-800 leading-relaxed">&ldquo;{r.text}&rdquo;</p>
                <div className="flex items-center gap-3 mt-6 pt-5 border-t border-cream-100">
                  <img src={r.avatar} alt={r.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                    <p className="text-xs text-slate-600">{r.country}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LOCATION */}
      <section id="dove-siamo" className="bg-white border-y border-cream-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-navy-700 font-semibold mb-4">{t("loc.eyebrow")}</p>
            <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 display-tight-2">{t("loc.title")}</h2>
            <p className="text-slate-600 mt-6 leading-relaxed">
              {t("loc.intro")}
            </p>
            <ul className="mt-6 space-y-3 text-sm text-slate-800">
              {[
                "Metro M2 — 4 min a piedi",
                "Stazione Centrale Milano — 8 min a piedi",
                "Corso Buenos Aires — 5 min a piedi",
                "Duomo di Milano — 15 min in metro",
                "Bosco Verticale & Porta Nuova — 20 min a piedi",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-navy-600 mt-2" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={120}>
            <div className="rounded-2xl overflow-hidden border border-cream-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <iframe
                title="Mappa Attico Skyline Milano"
                src="https://www.openstreetmap.org/export/embed.html?bbox=9.205%2C45.483%2C9.220%2C45.490&layer=mapnik&marker=45.4862%2C9.2113"
                className="w-full h-[440px] border-0"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 lg:px-12 py-24 md:py-32">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-navy-700 font-semibold mb-4 text-center">{t("faq.eyebrow")}</p>
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 display-tight-2 text-center">{t("faq.title")}</h2>
        </Reveal>
        <Accordion type="single" collapsible className="mt-12">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-cream-200">
              <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-lg font-semibold text-slate-900 tracking-tight hover:text-navy-700 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed text-[15px]">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 lg:px-12 pb-24">
        <Reveal>
          <div className="max-w-7xl mx-auto bg-navy-50 rounded-3xl px-8 md:px-16 py-16 md:py-24 text-center">
            <h2 className="text-4xl md:text-6xl font-extrabold text-slate-900 display-tight-2">{t("cta.title")}</h2>
            <p className="text-slate-600 mt-5 max-w-xl mx-auto leading-relaxed">
              {t("cta.subtitle")}
            </p>
            <Link
              to="/prenotazione"
              data-testid="final-cta-prenota"
              className="inline-flex items-center gap-2 mt-10 rounded-full bg-slate-900 hover:bg-slate-800 text-cream-50 px-9 py-4 font-semibold transition-all hover:-translate-y-0.5"
            >
              {t("bw.submit")} <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>

      <Footer />
      <MobileBottomCTA price={apt.price_per_night} />
      <Lightbox
        images={apt.gallery}
        startIndex={lightbox.index}
        open={lightbox.open}
        onClose={() => setLightbox({ open: false, index: 0 })}
      />
    </div>
  );
}
